import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  focus,
  ink,
  inkOn,
  inset,
  line,
  radius,
  rgba,
  semantic,
  shadow,
  space,
  surface,
  typeScale,
  weight,
} from '@repo/design-tokens';
import { Button, Icon, Note, Table } from '@repo/design-system';
import type { DropZoneTarget, SelectOption, TableColumn } from '@repo/design-system';
import { ColumnMapCaptions, ColumnMapRow, IconDisc, TagSwitcher } from '@repo/ui';
import type { TagSwitcherOption } from '@repo/ui';
import {
  LEAVE_OUT,
  columnMapCounts,
  columnMapReady,
  columnOptions,
  previewRows,
  blankMappedRecords,
} from '@repo/front-end-utils';
import type { AssetCsvContract, ColumnMapEntry, ParsedAssetCsv } from '@repo/front-end-utils';

import EquipmentUploadValues, { valuesFootNote } from './EquipmentUploadValues';
import type { EquipmentUploadValuesProps } from './EquipmentUploadValues';

/**
 * **Upload 1: map the columns** (`equipment-page-upload-columns`, frame 02 of Rob's 12 October
 * *Equipment Page* design): a customer's file, read in the browser, with every header against the
 * TwinLabs column it will fill, a preview of the first rows as they will be read, and the
 * *Columns · Values · Upload* steps.
 *
 * A page composition, like `EquipmentStart`, built from design-system parts — `ColumnMapRow`,
 * `TagSwitcher`, `Table`, `Note`, `Button`, `IconDisc`. It holds no upload state:
 * `AssetCsvUploader` owns the file, the decisions, the edits, the check and the upload, and passes
 * the Values step's answer and its status region in. The Values body is `EquipmentUploadValues`
 * (`equipment-page-upload-values`, frame 03).
 *
 * ## Values
 *
 * Read off frame 02 and its design file (`Equipment Page.dc.html`): a header strip; the map and a
 * 520 px preview card side by side, 18 px apart; column captions 10/14 mono in ink-4 with the
 * label tracking; a sticky footer.
 */

export type UploadStep = 'columns' | 'values' | 'upload';

export interface EquipmentUploadProps {
  fileName: string;
  /**
   * The column contract the file is read against (`equipment-page-adoption`, R5): `adopt` offers the
   * five adoption columns in each select and says above the map what an adoption file keeps.
   * Default customer.
   */
  contract?: AssetCsvContract;
  parsed: ParsedAssetCsv;
  entries: readonly ColumnMapEntry[];
  onDecide: (index: number, choice: string) => void;
  step: UploadStep;
  onColumns: () => void;
  onValues: () => void;
  /** The server check of the rewritten file is waiting or running: Upload shows it is busy. */
  checking: boolean;
  /**
   * The browser finds nothing and the server's latest answer for exactly this file refuses nothing
   * (`equipment-page-upload-values`, R10): the only state Upload is offered in.
   */
  ready: boolean;
  /** The Values step's cards and presses; `null` until Values is entered. */
  values: Omit<EquipmentUploadValuesProps, 'uploadRef'> | null;
  /** *Upload {rows} Assets* (R13). */
  onUpload: () => void;
  /** *Download With Problems Column* (R13a). */
  onDownload: () => void;
  /** The uploader's hold on *Upload {rows} Assets*, for focus after *Check again*. */
  uploadRef?: React.RefObject<HTMLButtonElement | null>;
  /** The start screen's target: the twin the rows go to. */
  target: DropZoneTarget;
  /** A replacement file from *Choose a Different File*. */
  onChooseFile: (file: File | undefined) => void;
  onCancel: () => void;
  disabled?: boolean;
  /** The no-Name note's *Download Template* (the start screen's hook). */
  download: { onClick: () => void; downloading: boolean };
  /** The start screen's own banners: register messages and a failed template download. */
  banners?: React.ReactNode;
  /** The uploader's always-mounted polite status region. */
  status: React.ReactNode;
}

/** The select's options for one row: technical name, plain label in the list, *Left out* once chosen. */
const selectOptions = (
  entries: readonly ColumnMapEntry[],
  index: number,
  contract: AssetCsvContract,
): SelectOption[] =>
  columnOptions(entries, index, contract).map(option => ({
    value: option.value,
    disabled: option.disabled,
    label:
      option.column === null ? (
        <>
          <span className="cm-in-list">Leave out</span>
          <span className="cm-in-trigger">Left out</span>
        </>
      ) : (
        <>
          {option.column}{' '}
          <span className="cm-in-list">
            {option.usedBy ? `used by ${option.usedBy}` : option.label}
          </span>
        </>
      ),
  }));

/** *Room and Owner need a decision* — three named, then a count (R11b). */
const decisionNote = (undecided: readonly string[]): string => {
  if (undecided.length === 0) return '';
  const verb = undecided.length === 1 ? 'needs' : 'need';
  if (undecided.length === 1) return `${undecided[0]} ${verb} a decision`;
  if (undecided.length <= 3) {
    return `${undecided.slice(0, -1).join(', ')} and ${undecided[undecided.length - 1]} ${verb} a decision`;
  }
  return `${undecided[0]}, ${undecided[1]} and ${undecided.length - 2} more need a decision`;
};

const PREVIEW_ROWS = 6;

type PreviewRow = { key: number; cells: string[] };

const EquipmentUpload: React.FC<EquipmentUploadProps> = ({
  fileName,
  contract = 'customer',
  parsed,
  entries,
  onDecide,
  step,
  onColumns,
  onValues,
  checking,
  ready: uploadReady,
  values,
  onUpload,
  onDownload,
  uploadRef: uploadRefProp,
  target,
  onChooseFile,
  onCancel,
  disabled = false,
  download,
  banners,
  status,
}) => {
  const rootRef = useRef<HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const checkValuesRef = useRef<HTMLButtonElement>(null);
  const valuesRef = useRef<HTMLDivElement>(null);
  const ownUploadRef = useRef<HTMLButtonElement>(null);
  const uploadRef = uploadRefProp ?? ownUploadRef;

  const rowCount = parsed.records.length;
  const counts = columnMapCounts(entries);
  const skipped = useMemo(() => blankMappedRecords(parsed, entries), [parsed, entries]);
  const ready = columnMapReady(entries);
  const columnsLeft = counts.undecided.length + (counts.nameMapped ? 0 : 1);

  /*
   * R14: arriving from the start screen, focus goes where the work is — the first header waiting
   * for a decision, else *Check Values* when it can take focus, else the first select (every header
   * decided but none names the assets: Check Values is disabled; Copilot, PR #2170). Once per file:
   * a new file remounts the panel.
   */
  useEffect(() => {
    const firstWaiting = rootRef.current?.querySelector<HTMLElement>(
      '[data-state="decide"] [role="combobox"]',
    );
    const checkValuesButton =
      checkValuesRef.current && !checkValuesRef.current.disabled ? checkValuesRef.current : null;
    const firstSelect = rootRef.current?.querySelector<HTMLElement>('[role="combobox"]');
    (firstWaiting ?? checkValuesButton ?? firstSelect)?.focus();
    // Once per file, by design: the mapping changing must not move focus off the select in use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileName]);

  /*
   * *Check Values* goes with the Columns footer, so focus moves into Values (`upload-values`, R17):
   * the open card's first action, else *Upload {rows} Assets* — busy, not disabled, while the server
   * checks, so it can hold focus — else the Values content itself.
   */
  const valuesFocusOwed = useRef(false);
  useEffect(() => {
    if (step !== 'values' || !valuesFocusOwed.current) return;
    valuesFocusOwed.current = false;
    const action = valuesRef.current?.querySelector<HTMLElement>(
      '.tl-check-card[data-open] .tl-cc-actions button:not(:disabled)',
    );
    const upload = uploadRef.current && !uploadRef.current.disabled ? uploadRef.current : null;
    (action ?? upload ?? valuesRef.current)?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `step` only: `uploadRef` is a ref read when focus moves
  }, [step]);
  const checkValues = () => {
    valuesFocusOwed.current = true;
    onValues();
  };

  /** Error kinds with lines left (R12). */
  const problemCount = values?.check.kinds.length ?? 0;
  const rowsToSend = values?.check.rows ?? rowCount - skipped;
  const uploading = step === 'upload';

  const steps: TagSwitcherOption[] = [
    {
      value: 'columns',
      text: 'Columns',
      icon: step === 'columns' ? 'circle-dot' : 'circle-check',
      ...(columnsLeft > 0 ? { count: columnsLeft } : {}),
      ariaLabel: `Columns, ${columnsLeft} left`,
    },
    {
      value: 'values',
      text: 'Values',
      icon: step === 'values' ? 'circle-dot' : 'circle',
      disabled: !ready,
      ...(problemCount > 0 ? { count: problemCount } : {}),
      ariaLabel: `Values, ${problemCount} left`,
    },
    {
      value: 'upload',
      text: step === 'upload' ? 'Uploading…' : 'Upload',
      icon: step === 'upload' ? 'circle-dot' : 'circle',
      disabled: true,
      ariaLabel: step === 'upload' ? 'Uploading' : 'Upload, not yet',
    },
  ];

  const preview = useMemo(() => previewRows(parsed, entries, PREVIEW_ROWS), [parsed, entries]);
  const previewColumns: TableColumn<PreviewRow>[] = preview.columns.map((column, i) => ({
    key: column,
    header: column,
    render: row => row.cells[i],
    resizable: false,
  }));
  const previewData: PreviewRow[] = preview.rows.map((cells, key) => ({ key, cells }));

  const columnsNote = [
    `${counts.mapped} of ${counts.listed} columns mapped`,
    decisionNote(counts.undecided),
    counts.nameMapped ? '' : 'no Name column',
  ]
    .filter(Boolean)
    .join(' · ');
  const valuesNote = values ? valuesFootNote(values.check, checking, uploadReady) : '';

  return (
    <Root ref={rootRef} aria-label={`Upload ${fileName}`} data-testid="equipment-upload">
      <Head>
        <IconDisc tone="aqua" size={30}>
          <Icon name="file-csv" variant="solid" size="sm" />
        </IconDisc>
        <div className="up-file">
          <div className="up-name">{fileName}</div>
          <div className="up-sub">
            {rowCount.toLocaleString('en-GB')} rows · {entries.length} columns · going to{' '}
            <strong>{target.name}</strong>
          </div>
        </div>
        <TagSwitcher
          className="up-steps"
          label="Upload steps"
          size="default"
          activation="manual"
          selectedValue={step}
          options={steps}
          onChange={value => (value === 'values' ? checkValues() : onColumns())}
        />
        <div className="up-actions">
          <Button
            type="button"
            variant="frosted"
            size="sm"
            disabled={disabled || step === 'upload'}
            iconLeft={<Icon name="arrow-right-arrow-left" variant="solid" size="xs" />}
            onClick={() => fileInputRef.current?.click()}
          >
            Choose a Different File
          </Button>
          <Button
            type="button"
            variant="text"
            size="sm"
            disabled={step === 'upload'}
            aria-label="Cancel this upload"
            iconLeft={<Icon name="xmark" variant="solid" size="xs" />}
            onClick={onCancel}
          >
            Cancel
          </Button>
          {/* The picker behind *Choose a Different File*: out of the tab order, the button is the
              control. */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            tabIndex={-1}
            aria-hidden="true"
            className="visually-hidden"
            onChange={event => {
              onChooseFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </div>
      </Head>

      {banners}

      {step === 'columns' ? (
        <Body>
          <div className="up-map">
            {/* The adoption banner, moved onto the map as a note (`equipment-page-adoption`, R5):
                what an adoption file keeps, and where each row's product comes from. */}
            {contract === 'adopt' && (
              <Note
                tone={semantic.status.roles.information.noteTone}
                icon={<Icon name="arrows-rotate" size="xs" />}
              >
                Adopting a register TwinLabs maintained. Its bid, reference_id, asset_name, model_3d
                and capacity columns are kept, not regenerated, so these assets go live with the
                BIDs the customer and our reporting already use. Each row&rsquo;s product comes from
                BI first, then the file&rsquo;s reference_id, then a lookup. Only use this for a
                file we wrote.
              </Note>
            )}
            {!counts.nameMapped && (
              <Note
                tone={semantic.status.roles.problem.noteTone}
                icon={<Icon name="circle-info" size="xs" />}
              >
                <span>
                  This file has no Name column. Choose the column that names each asset, or start
                  from the template.
                </span>
                <span className="up-note-action">
                  <Button
                    type="button"
                    variant="text"
                    size="xs"
                    loading={download.downloading}
                    iconLeft={<Icon name="download" variant="solid" size="xs" />}
                    onClick={download.onClick}
                  >
                    Download Template
                  </Button>
                </span>
              </Note>
            )}
            {skipped > 0 && (
              <Note
                tone={semantic.status.roles.information.noteTone}
                icon={<Icon name="circle-info" size="xs" />}
              >
                {skipped === 1
                  ? '1 row has nothing in the columns being kept, so it will not be imported.'
                  : `${skipped.toLocaleString('en-GB')} rows have nothing in the columns being kept, so they will not be imported.`}
              </Note>
            )}
            {!counts.identifierMapped && (
              <Note
                tone={semantic.status.roles.information.noteTone}
                icon={<Icon name="circle-info" size="xs" />}
              >
                Without an Asset Identifier column, uploading an updated file later will add every
                row again instead of updating it.
              </Note>
            )}
            <ColumnMapCaptions />
            {entries.map(entry => (
              <ColumnMapRow
                key={entry.index}
                id={`column-map-${entry.index}`}
                header={entry.display}
                firstValue={entry.firstValue}
                state={entry.state}
                value={
                  entry.state === 'decide'
                    ? undefined
                    : entry.state === 'out'
                      ? LEAVE_OUT
                      : (entry.column ?? undefined)
                }
                reason={entry.reason}
                options={selectOptions(entries, entry.index, contract)}
                onChange={choice => onDecide(entry.index, choice)}
                disabled={disabled}
              />
            ))}
          </div>
          <section className="up-preview" aria-labelledby="up-preview-title">
            <div className="up-preview-head">
              <h3 id="up-preview-title">How we&rsquo;ll read it</h3>
              <span>
                first {Math.min(PREVIEW_ROWS, rowCount)} of {rowCount.toLocaleString('en-GB')} rows
              </span>
            </div>
            {previewColumns.length > 0 ? (
              <Table<PreviewRow>
                aria-label="How we'll read it"
                colourMode="none"
                resizable={false}
                columns={previewColumns}
                data={previewData}
                keyExtractor={row => row.key}
              />
            ) : (
              <p className="up-preview-empty">
                Nothing yet: map a Name or an Asset Identifier to see the rows.
              </p>
            )}
            <Note
              tone={semantic.status.roles.information.noteTone}
              icon={<Icon name="circle-info" size="xs" />}
            >
              Manufacturer, model and category are clues. We keep them as you wrote them and use
              them to identify each machine.
            </Note>
          </section>
        </Body>
      ) : (
        <Values ref={valuesRef} tabIndex={-1} role="group" aria-label="Values">
          {values && (
            <EquipmentUploadValues
              {...values}
              disabled={disabled || uploading || values.disabled}
              uploadRef={uploadRef}
            />
          )}
        </Values>
      )}

      <Foot>
        <span className="up-safe">
          <Icon name="shield-halved" variant="solid" size="xs" />
          Nothing has been uploaded yet
        </span>
        <span className="up-foot-note">{step === 'columns' ? columnsNote : valuesNote}</span>
        <span className="up-foot-action">
          {step === 'columns' ? (
            <Button
              ref={checkValuesRef}
              type="button"
              variant="primary"
              size="inline"
              disabled={!ready || disabled}
              iconLeft={<Icon name="arrow-right" variant="solid" size="xs" />}
              onClick={checkValues}
            >
              Check Values
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="frosted"
                size="inline"
                disabled={disabled || uploading || !values}
                aria-label="Download a copy with a problems column"
                iconLeft={<Icon name="download" variant="solid" size="xs" />}
                onClick={onDownload}
              >
                Download With Problems Column
              </Button>
              {/* Busy rather than disabled while the server checks, so it can hold focus (R17); the
                  Button guards activation while loading. */}
              <Button
                ref={uploadRef}
                type="button"
                variant="primary"
                size="inline"
                loading={checking || uploading}
                disabled={disabled || (!uploadReady && !checking && !uploading)}
                aria-label={`Upload ${rowsToSend.toLocaleString('en-GB')} assets`}
                iconLeft={<Icon name="upload" variant="solid" size="xs" />}
                onClick={onUpload}
              >
                Upload {rowsToSend.toLocaleString('en-GB')} Assets
              </Button>
            </>
          )}
        </span>
      </Foot>
      {status}
    </Root>
  );
};

const Root = styled.section.attrs({ role: 'region' })`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  flex: 1;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[3]};
  padding: ${space[3]} ${space[4]};
  border-bottom: 1px solid ${line[4]};

  .up-file {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .up-name {
    font-size: ${typeScale.body1.fontSize};
    line-height: ${typeScale.body1.lineHeight};
    font-weight: ${weight.semibold};
    color: ${ink[1]};
    overflow-wrap: anywhere;
  }
  .up-sub {
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
    color: ${ink[3]};
  }
  .up-sub strong {
    font-weight: ${weight.semibold};
    color: ${ink[1]};
  }
  .up-steps {
    margin-left: ${space[6]};
  }
  .up-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: ${space[2]};
  }
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 520px;
  gap: ${inset[5]};
  padding: ${space[4]} ${inset[5]};
  align-items: start;

  .up-map {
    display: flex;
    flex-direction: column;
    gap: ${inset[2]};
    min-width: 0;
  }
  .up-map > .tl-note,
  .up-map > [class*='Note'] {
    margin-bottom: ${space[1]};
  }
  .up-note-action {
    display: block;
    margin-top: ${space[1]};
  }

  .up-preview {
    display: flex;
    flex-direction: column;
    gap: ${inset[3]};
    padding: ${inset[4]};
    border-radius: ${radius.lg};
    background: ${rgba(surface[0], 0.7)};
    border: 1px solid ${rgba(surface[0], 0.95)};
    box-shadow: ${shadow.glassSm};
  }
  .up-preview-head {
    display: flex;
    align-items: baseline;
    gap: ${space[2]};
  }
  .up-preview-head h3 {
    margin: 0;
    font-size: ${typeScale.body1.fontSize};
    line-height: ${typeScale.body1.lineHeight};
    font-weight: ${weight.semibold};
    color: ${ink[1]};
  }
  .up-preview-head span {
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
    color: ${ink[3]};
  }
  .up-preview-empty {
    margin: 0;
    font-size: ${typeScale.caption1.fontSize};
    color: ${ink[3]};
  }

  /* One column under the panel's width — the preview goes beneath the map. */
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Values = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  padding: ${space[4]} ${inset[5]};
  outline: none;
  border-radius: ${radius.lg};

  /* It takes focus after *Check Values* (R14), so a keyboard user sees where it went. */
  &:focus-visible {
    box-shadow: ${focus.ring};
  }
`;

const Foot = styled.div`
  position: sticky;
  /*
   * Into the scrolling card's bottom padding: the Equipment step's main card (\`Card\` \`md\`, 16 px
   * padding, \`space[4]\`) is what scrolls, and a sticky footer at \`bottom: 0\` stops at the inside of
   * that padding, leaving a 16 px band where rows showed through below it (review). Re-measure if
   * the host card's size changes.
   */
  bottom: calc(-1 * ${space[4]});
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: ${space[3]};
  padding: ${space[3]} ${space[4]};
  border-top: 1px solid ${line[4]};
  /* Solid: the map scrolls under it, and a translucent strip let the rows show through. */
  background: ${surface[0]};
  font-size: ${typeScale.caption1.fontSize};
  line-height: ${typeScale.caption1.lineHeight};

  .up-safe {
    display: inline-flex;
    align-items: center;
    gap: ${space[2]};
    color: ${ink[2]};
  }
  .up-safe > :first-child {
    color: ${inkOn.mint};
  }
  .up-foot-note {
    color: ${ink[3]};
  }
  .up-foot-action {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: ${space[2]};
  }
`;

export default EquipmentUpload;
